import { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { FileInput, Plus, Search, User, Package, Clock, CheckCircle, XCircle, AlertCircle, X, ChevronDown, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Async Searchable Dropdown for Commodities (searches server-side)
function AsyncCommodityDropdown({ label, value, selectedDisplay, onSelect, placeholder, required, disabled, testId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeout = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Search commodities when user types (debounced)
  useEffect(() => {
    if (searchTerm.length >= 2) {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(async () => {
        setLoading(true);
        try {
          const response = await axios.get(`${API}/api/master-data/stores/commodities/search`, {
            params: { q: searchTerm, limit: 50 },
            
          });
          setOptions(response.data.data || []);
        } catch (error) {
          console.error('Search error:', error);
          setOptions([]);
        }
        setLoading(false);
      }, 300);
    } else {
      setOptions([]);
    }
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [searchTerm]);
  
  const handleSelect = (commodity) => {
    onSelect(commodity);
    setIsOpen(false);
    setSearchTerm('');
    setOptions([]);
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-4 py-2.5 border rounded-xl text-sm text-left flex items-center justify-between transition ${
          disabled ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
            : 'border-gray-200 hover:border-[#1a237e] focus:border-[#1a237e]'
        }`}
        data-testid={testId}
      >
        <span className={selectedDisplay ? 'text-gray-900' : 'text-gray-400'}>
          {selectedDisplay || placeholder}
        </span>
        <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type at least 2 characters to search..."
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#1a237e] outline-none"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                <RefreshCw size={16} className="animate-spin inline mr-2" />
                Searching...
              </div>
            ) : searchTerm.length < 2 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                Type at least 2 characters to search commodities
              </div>
            ) : options.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">No commodities found for "{searchTerm}"</div>
            ) : (
              options.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => handleSelect(opt)}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-indigo-50 transition"
                >
                  <div className="font-medium text-gray-900">{opt.commodityName}</div>
                  <div className="text-xs text-gray-500">HSN: {opt.hsnCode || opt.commodityCode} | GST: {opt.gstRate || 18}%</div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Searchable Dropdown Component
function SearchableDropdown({ label, value, options, onChange, placeholder, displayField, valueField, required, disabled, testId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  
  const selectedOption = options.find(opt => opt[valueField] === value);
  const displayValue = selectedOption ? selectedOption[displayField] : '';
  
  const filteredOptions = options.filter(opt => 
    opt[displayField]?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-4 py-2.5 border rounded-xl text-sm text-left flex items-center justify-between transition ${
          disabled ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
            : 'border-gray-200 hover:border-[#1a237e] focus:border-[#1a237e] focus:ring-2 focus:ring-[#1a237e]/20'
        }`}
        data-testid={testId}
      >
        <span className={displayValue ? 'text-gray-900' : 'text-gray-400'}>{displayValue || placeholder}</span>
        <ChevronDown size={16} className={`text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <Search size={14} className="text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search commodities..."
                className="flex-1 bg-transparent border-none outline-none text-sm"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">No commodities found</div>
            ) : (
              filteredOptions.map((opt, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => { onChange(opt[valueField], opt); setIsOpen(false); setSearchTerm(''); }}
                  className={`w-full px-3 py-2.5 text-left text-sm hover:bg-[#1a237e]/10 transition ${
                    opt[valueField] === value ? 'bg-[#1a237e]/20 text-[#1a237e] font-medium' : 'text-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{opt[displayField]}</span>
                    {opt.category && <span className="text-xs text-gray-400">{opt.category}</span>}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MaterialRequisitions() {
  const { API, user } = useContext(AppContext);
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [saveStatus, setSaveStatus] = useState(null);
  
  // Review Modal State
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);
  const [reviewAction, setReviewAction] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewRemarks, setReviewRemarks] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  // Employee list from Employment Directory
  const [employees, setEmployees] = useState([]);

  const [newReq, setNewReq] = useState({
    employeeName: '',
    employeeId: '',
    department: '',
    commodityId: '',
    consumable: '',
    uom: '',
    quantity: '',
    reason: '',
    urgency: 'Normal'
  });
  const [uomList, setUomList] = useState([]); // UOM from master data

  useEffect(() => {
    fetchRequisitions();
    fetchUomList();
    fetchEmployees();
  }, [filter]);

  const fetchRequisitions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stores/requisitions`, {
        params: { status: filter },
        
      });
      setRequisitions(response.data);
    } catch (error) {
      console.error('Error fetching requisitions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUomList = async () => {
    try {
      const response = await axios.get(`${API}/master-data/stores/uom`);
      setUomList(response.data.data || []);
    } catch (error) {
      console.error('Error fetching UOM:', error);
    }
  };

  // Fetch employees from Employment Directory (HR)
  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/hr/employees`);
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Handle employee selection from dropdown
  const handleEmployeeSelect = (employeeId) => {
    const selectedEmployee = employees.find(emp => emp.id === employeeId);
    if (selectedEmployee) {
      setNewReq({
        ...newReq,
        employeeId: selectedEmployee.id,
        employeeName: selectedEmployee.name,
        department: selectedEmployee.department || newReq.department
      });
    }
  };

  const handleCommoditySelect = (commodityId, commodity) => {
    setNewReq({
      ...newReq,
      commodityId: commodityId,
      consumable: commodity.commodityName || '',
      uom: newReq.uom || 'KGS' // Keep existing UOM or default
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Issued': return 'bg-green-100 text-green-700 border-green-200';
      case 'Pending': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Rejected': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'Urgent': return 'bg-red-100 text-red-700';
      case 'High': return 'bg-orange-100 text-orange-700';
      case 'Normal': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Open review modal for a requisition
  const openReviewModal = (req, action) => {
    setSelectedReq(req);
    setReviewAction(action);
    setRejectionReason('');
    setReviewRemarks('');
    setShowReviewModal(true);
  };

  // Handle review submission (approve or reject)
  const handleReviewSubmit = async () => {
    if (!selectedReq) return;
    
    if (reviewAction === 'reject' && !rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    setReviewLoading(true);
    try {
      await axios.put(`${API}/api/stores/requisitions/${selectedReq.id}/review`, {
        action: reviewAction,
        reviewerName: user?.name || 'Admin User',
        rejectionReason: rejectionReason,
        remarks: reviewRemarks
      });
      
      setShowReviewModal(false);
      setSelectedReq(null);
      fetchRequisitions();
    } catch (error) {
      console.error('Review error:', error);
      alert('Failed to process review. Please try again.');
    }
    setReviewLoading(false);
  };

  const handleSubmitReq = async () => {
    setSaveStatus('saving');
    try {
      const req = {
        ...newReq,
        quantity: parseInt(newReq.quantity),
        unit: newReq.uom || 'pcs',
        status: 'Pending',
        requestDate: new Date().toISOString().split('T')[0],
        approvedBy: '-'
      };
      
      await axios.post(`${API}/stores/requisitions`, req);
      setSaveStatus('saved');
      setTimeout(() => {
        setSaveStatus(null);
        setShowAddModal(false);
        fetchRequisitions();
        resetForm();
      }, 1500);
    } catch (error) {
      console.error('Error submitting requisition:', error);
      // Fallback to local state if API fails
      const req = {
        ...newReq,
        quantity: parseInt(newReq.quantity),
        unit: newReq.uom || 'pcs',
        status: 'Pending',
        requestDate: new Date().toISOString().split('T')[0],
        approvedBy: '-'
      };
      setRequisitions([req, ...requisitions]);
      setSaveStatus('saved');
      setTimeout(() => {
        setSaveStatus(null);
        setShowAddModal(false);
        resetForm();
      }, 1500);
    }
  };

  const resetForm = () => {
    setNewReq({
      employeeName: '',
      employeeId: '',
      department: '',
      commodityId: '',
      consumable: '',
      uom: '',
      quantity: '',
      reason: '',
      urgency: 'Normal'
    });
  };

  const stats = {
    total: requisitions.length,
    pending: requisitions.filter(r => r.status === 'Pending').length,
    approved: requisitions.filter(r => r.status === 'Approved').length,
    issued: requisitions.filter(r => r.status === 'Issued').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1a237e] border-t-transparent"></div>
        <span className="ml-3 text-gray-600">Loading requisitions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="material-requisitions-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-[#1a237e] to-[#283593] rounded-lg">
            <FileInput className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Material Requisitions</h1>
            <p className="text-sm text-gray-500">Submit and track consumable requests from Commodity Master Data</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => { fetchRequisitions(); }}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            title="Refresh"
          >
            <RefreshCw size={16} className="text-gray-600" />
          </button>

          <button 
            onClick={() => setShowAddModal(true)} 
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1a237e] to-[#283593] text-white rounded-lg hover:from-[#0d1757] hover:to-[#1a237e] transition text-sm font-medium shadow-md"
            data-testid="add-requisition-btn"
          >
            <Plus size={16} />
            <span>New Requisition</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <p className="text-xs text-gray-500">Total Requests</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-1 mb-1">
            <AlertCircle size={14} className="opacity-80" />
            <p className="text-xs opacity-80">Pending</p>
          </div>
          <p className="text-2xl font-bold">{stats.pending}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-1 mb-1">
            <CheckCircle size={14} className="opacity-80" />
            <p className="text-xs opacity-80">Approved</p>
          </div>
          <p className="text-2xl font-bold">{stats.approved}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <div className="flex items-center gap-1 mb-1">
            <Package size={14} className="opacity-80" />
            <p className="text-xs opacity-80">Issued</p>
          </div>
          <p className="text-2xl font-bold">{stats.issued}</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center space-x-2 overflow-x-auto pb-2">
        {['all', 'Pending', 'Approved', 'Issued', 'Rejected'].map((f) => (
          <button 
            key={f} 
            onClick={() => setFilter(f)} 
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              filter === f 
                ? 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white shadow-md' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f === 'all' ? 'All' : f}
          </button>
        ))}
      </div>

      {/* Requisitions Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-3 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50">
          <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
            <Package size={16} className="text-[#1a237e]" />
            Requisition Records - Commodity from Master Data
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">Req ID</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">Employee</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">Department</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">Commodity</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700">Quantity</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">Reason</th>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-700">Urgency</th>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-700">Status</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700">Date</th>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {requisitions.filter(r => filter === 'all' || r.status === filter).length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                    No requisitions found
                  </td>
                </tr>
              ) : (
                requisitions.filter(r => filter === 'all' || r.status === filter).map((req) => (
                  <tr key={req.id} className="hover:bg-indigo-50/50 transition-colors" data-testid={`req-row-${req.id}`}>
                    <td className="px-3 py-3 font-mono text-[#1a237e] font-medium">{req.id}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center space-x-2">
                        <div className="h-7 w-7 bg-gradient-to-br from-[#e65100] to-[#f57c00] rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-[10px]">{req.employeeName?.charAt(0) || '?'}</span>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-900">{req.employeeName}</p>
                          <p className="text-[10px] text-gray-500">{req.employeeId}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-gray-600">{req.department}</td>
                    <td className="px-3 py-3 font-medium text-gray-900">{req.consumable}</td>
                    <td className="px-3 py-3 text-right font-semibold">{req.quantity} {req.unit}</td>
                    <td className="px-3 py-3 text-gray-600 max-w-xs truncate" title={req.reason}>{req.reason}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getUrgencyColor(req.urgency)}`}>
                        {req.urgency}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${getStatusColor(req.status)}`}>
                          {req.status}
                        </span>
                        {req.approvedBy && req.approvedBy !== '-' && (
                          <span className="text-[9px] text-gray-500">by {req.approvedBy}</span>
                        )}
                        {req.rejectedBy && (
                          <span className="text-[9px] text-red-500" title={req.rejectionReason}>by {req.rejectedBy}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-gray-500">{req.requestDate}</td>
                    <td className="px-3 py-3">
                      {req.status === 'Pending' ? (
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openReviewModal(req, 'approve')}
                            className="px-2 py-1 bg-green-100 text-green-700 rounded text-[10px] font-medium hover:bg-green-200 transition flex items-center gap-1"
                            data-testid={`approve-btn-${req.id}`}
                          >
                            <CheckCircle size={12} />
                            Approve
                          </button>
                          <button
                            onClick={() => openReviewModal(req, 'reject')}
                            className="px-2 py-1 bg-red-100 text-red-700 rounded text-[10px] font-medium hover:bg-red-200 transition flex items-center gap-1"
                            data-testid={`reject-btn-${req.id}`}
                          >
                            <XCircle size={12} />
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-[10px] text-gray-400 text-center block">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Requisition Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <FileInput className="text-[#1a237e]" size={20} />
                <h2 className="font-bold text-lg">New Material Requisition</h2>
              </div>
              <button onClick={() => { setShowAddModal(false); resetForm(); }} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Employee Selection from Employment Directory */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee Name <span className="text-red-500">*</span>
                    <span className="text-xs text-gray-400 ml-1">(from Employment Directory)</span>
                  </label>
                  <select 
                    value={newReq.employeeId} 
                    onChange={(e) => handleEmployeeSelect(e.target.value)} 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-[#1a237e] outline-none"
                    data-testid="employee-name-input"
                  >
                    <option value="">Select employee...</option>
                    {employees.filter(emp => emp.status === 'Active').map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} - {emp.designation || emp.department}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
                  <input 
                    type="text" 
                    value={newReq.employeeId} 
                    readOnly
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-gray-600" 
                    placeholder="Auto-filled from selection" 
                    data-testid="employee-id-input"
                  />
                </div>
              </div>

              {/* Department - Auto-filled from employee selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-400 ml-1">(auto-filled or select)</span>
                </label>
                <select 
                  value={newReq.department} 
                  onChange={(e) => setNewReq({...newReq, department: e.target.value})} 
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-[#1a237e] outline-none"
                  data-testid="department-select"
                >
                  <option value="">Select department</option>
                  <option value="Production">Production</option>
                  <option value="Quality">Quality</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Dispatch">Dispatch</option>
                  <option value="Stores">Stores</option>
                  <option value="HR">HR</option>
                  <option value="Accounts">Accounts</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>

              {/* Commodity Selection from Master Data - Async Search */}
              <div>
                <AsyncCommodityDropdown
                  label="Commodity Required"
                  value={newReq.commodityId}
                  selectedDisplay={newReq.commodityId ? `${newReq.consumable || 'Selected'}` : ''}
                  onSelect={(commodity) => handleCommoditySelect(commodity.id, commodity)}
                  placeholder="Search commodity by name or HSN..."
                  required={true}
                  testId="commodity-dropdown"
                />
              </div>
              
              {/* Editable Material Name - Always visible */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Material Name <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-400 ml-2">(Editable - you can shorten)</span>
                </label>
                <input
                  type="text"
                  value={newReq.consumable}
                  onChange={(e) => setNewReq({...newReq, consumable: e.target.value})}
                  placeholder="Enter or edit material name..."
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-[#1a237e] outline-none"
                />
              </div>

              {/* Quantity and UOM in same row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity <span className="text-red-500">*</span></label>
                  <input 
                    type="number" 
                    value={newReq.quantity} 
                    onChange={(e) => setNewReq({...newReq, quantity: e.target.value})} 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-[#1a237e] outline-none" 
                    placeholder="0" 
                    data-testid="quantity-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">UOM <span className="text-red-500">*</span></label>
                  <select
                    value={newReq.uom}
                    onChange={(e) => setNewReq({...newReq, uom: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-[#1a237e] outline-none"
                    data-testid="uom-dropdown"
                  >
                    <option value="">Select UOM</option>
                    {uomList.length > 0 ? (
                      uomList.map(u => (
                        <option key={u.id} value={u.uomCode}>{u.uomCode} - {u.uomName}</option>
                      ))
                    ) : (
                      <>
                        <option value="KGS">KGS - Kilograms</option>
                        <option value="MT">MT - Metric Tons</option>
                        <option value="NOS">NOS - Numbers</option>
                        <option value="PCS">PCS - Pieces</option>
                        <option value="LTR">LTR - Liters</option>
                        <option value="SET">SET - Sets</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              {/* Urgency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Urgency</label>
                <select 
                  value={newReq.urgency} 
                  onChange={(e) => setNewReq({...newReq, urgency: e.target.value})} 
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-[#1a237e] outline-none"
                >
                  <option value="Normal">Normal</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Requisition <span className="text-red-500">*</span></label>
                <textarea 
                  value={newReq.reason} 
                  onChange={(e) => setNewReq({...newReq, reason: e.target.value})} 
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl resize-none focus:border-[#1a237e] outline-none" 
                  rows={3} 
                  placeholder="Explain why this material is needed..." 
                  data-testid="reason-textarea"
                />
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button 
                onClick={() => { setShowAddModal(false); resetForm(); }} 
                className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmitReq} 
                disabled={saveStatus === 'saving' || !newReq.employeeName || !newReq.employeeId || !newReq.department || !newReq.consumable || !newReq.uom || !newReq.quantity || !newReq.reason}
                className={`px-4 py-2.5 rounded-xl font-medium transition ${
                  saveStatus === 'saved' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white hover:from-[#0d1757] hover:to-[#1a237e]'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                data-testid="submit-requisition-btn"
              >
                {saveStatus === 'saving' ? 'Submitting...' : saveStatus === 'saved' ? 'Submitted!' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal - Approve/Reject Requisition */}
      {showReviewModal && selectedReq && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            <div className={`p-4 border-b ${reviewAction === 'approve' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {reviewAction === 'approve' ? (
                    <CheckCircle className="text-green-600" size={24} />
                  ) : (
                    <XCircle className="text-red-600" size={24} />
                  )}
                  <h2 className="font-bold text-lg">
                    {reviewAction === 'approve' ? 'Approve Requisition' : 'Reject Requisition'}
                  </h2>
                </div>
                <button 
                  onClick={() => setShowReviewModal(false)} 
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Requisition Details Summary */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Req ID:</span>
                  <span className="text-sm font-mono font-medium text-[#1a237e]">{selectedReq.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Requested By:</span>
                  <span className="text-sm font-medium">{selectedReq.employeeName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Department:</span>
                  <span className="text-sm font-medium">{selectedReq.department}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Material:</span>
                  <span className="text-sm font-medium">{selectedReq.consumable}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Quantity:</span>
                  <span className="text-sm font-medium">{selectedReq.quantity} {selectedReq.unit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Urgency:</span>
                  <span className={`text-sm font-medium px-2 py-0.5 rounded ${getUrgencyColor(selectedReq.urgency)}`}>
                    {selectedReq.urgency}
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <span className="text-sm text-gray-500">Reason:</span>
                  <p className="text-sm mt-1">{selectedReq.reason}</p>
                </div>
              </div>

              {/* Rejection Reason (only for reject action) */}
              {reviewAction === 'reject' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rejection Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a reason for rejecting this requisition..."
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-red-400 outline-none resize-none"
                    rows={3}
                  />
                </div>
              )}

              {/* Remarks (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remarks <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={reviewRemarks}
                  onChange={(e) => setReviewRemarks(e.target.value)}
                  placeholder="Any additional remarks..."
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-[#1a237e] outline-none"
                />
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button 
                onClick={() => setShowReviewModal(false)} 
                className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button 
                onClick={handleReviewSubmit}
                disabled={reviewLoading || (reviewAction === 'reject' && !rejectionReason.trim())}
                className={`px-4 py-2.5 rounded-xl font-medium transition flex items-center gap-2 ${
                  reviewAction === 'approve'
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-red-600 text-white hover:bg-red-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                data-testid="confirm-review-btn"
              >
                {reviewLoading ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : reviewAction === 'approve' ? (
                  <CheckCircle size={16} />
                ) : (
                  <XCircle size={16} />
                )}
                {reviewLoading ? 'Processing...' : reviewAction === 'approve' ? 'Approve Requisition' : 'Reject Requisition'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaterialRequisitions;
